import React from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { FiCheck, FiArrowRight, FiBriefcase, FiUserPlus, FiAtSign, FiSend } from "react-icons/fi";
import { fetchWithAuth, errorText } from "../utils/authFetch";
import { creditsChanged, formatCredits, useCredits } from "../utils/useCredits";
import { useAuth } from "../context/AuthContext";
import { usePlan, isInhouse } from "../utils/usePlan";
import { PLANS, usd, annualTotal, ANNUAL_DISCOUNT, planFeatureList, TOPUP_BLOCK_CREDITS, TOPUP_BLOCK_USD, ADDON_SETUP_USD, ADDON_MONTHLY_USD } from "./PricingPage";
import { DemoModal } from "./LandingPage";

/* Plans and payment on one page, the order in a side panel throughout.
   - Buying a plan: Plan (tier, seats, billing period) → Add-ons (extra credits, tracked
     companies) → Pay (who's covered, invoice email, terms). Airwallex's hosted page is only
     reached from the last step and `?done=1` is the way back.
   - Changing a live plan: Plan → Review; upgrades charge the saved card, the rest waits
     for renewal. `/plans` redirects here.
   - `?topup=1` is the one-page extra-credits purchase. */

const DONE_POLL_MS = 2000;
const DONE_POLL_LIMIT = 45;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_SEATS = 50;
const MAX_BLOCKS = 50;
const MAX_FIRMS = 500;
const EXTRA_STEP = 5; // blocks per click on the extra-credits counter
const EMAILS_KEY = "checkout.seatEmails"; // teammate emails survive the Airwallex round trip

// Where the server sends the browser after opening a checkout: Airwallex's hosted page (live or demo), or,
// for house-billed internal accounts whose checkout completes at once, straight back to this page. Anything
// else is refused; our own page is reached by path so the host the server names never matters.
function checkoutDestination(url) {
  try {
    const u = new URL(url, window.location.origin);
    if (u.protocol === "https:" && (u.hostname === "airwallex.com" || u.hostname.endsWith(".airwallex.com"))) return u.href;
    if (u.pathname === "/checkout") return u.pathname + u.search;
    return null;
  } catch {
    return null;
  }
}

const paidPlans = PLANS.filter((p) => p.monthly != null && !p.trial);
const enterprise = PLANS.find((p) => p.monthly == null);
const planOf = (id) => paidPlans.find((p) => p.id === id) || paidPlans.find((p) => p.featured) || paidPlans[0];

// The seats in the URL: "id:plan,new:plan,…" (a bare "plan" means a new seat).
function parseSeats(raw, fallbackPlan) {
  const text = String(raw || "").trim();
  const list = text
    .split(",")
    .map((part) => {
      const [id, plan] = part.includes(":") ? part.split(":") : ["new", part];
      return paidPlans.some((p) => p.id === plan) ? { id: id && id !== "new" ? id : null, plan } : null;
    })
    .filter(Boolean)
    .slice(0, MAX_SEATS);
  return list.length ? list : [{ id: null, plan: planOf(fallbackPlan).id }];
}

// Seats grouped by tier, in catalogue order: [[plan, count], …].
const tiersOf = (seats) => paidPlans.map((p) => [p, seats.filter((s) => s.plan === p.id).length]).filter(([, n]) => n > 0);
const tierLabel = (seats) => tiersOf(seats).map(([p, n]) => `${n}× ${p.name}`).join(", ");
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const perSeatMonth = (p, yearly) => (yearly ? Math.round(annualTotal(p.monthly) / 12) : p.monthly);
const perSeatCharge = (p, yearly) => (yearly ? annualTotal(p.monthly) : p.monthly);

// Next renewal, for the "from" line under the total.
const nextRenewal = (yearly) => {
  const d = new Date();
  if (yearly) d.setFullYear(d.getFullYear() + 1);
  else d.setMonth(d.getMonth() + 1);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const longDate = (v) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : null;
};

const initialsOf = (name, email) => {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  const base = words[0] || String(email || "");
  return base.slice(0, 2).toUpperCase();
};

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const spin = {
  display: "inline-block",
  width: 14,
  height: 14,
  border: "2px solid currentColor",
  borderTopColor: "transparent",
  borderRadius: "50%",
  marginRight: 8,
  verticalAlign: -2,
  animation: "ck-spin .7s linear infinite",
};

/* ---------------------------------------------------------------- styles */

/* MainContent already pads the pane; the flow is a centred column inside it. */
const Page = styled.main`
  width: 100%;
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 0 48px;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.ink};
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
`;

const Steps = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .rule {
    width: 28px;
    height: 1px;
    background: ${({ theme }) => theme.lineStrong};
  }
`;

const StepButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 0;
  padding: 4px 0;
  font: inherit;
  cursor: ${({ $done }) => ($done ? "pointer" : "default")};

  .dot {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 11px;
    font-weight: 600;
    background: ${({ theme, $on }) => ($on ? theme.accent : theme.surface)};
    color: ${({ theme, $on }) => ($on ? theme.onFill : theme.inkMuted)};
    box-shadow: ${({ theme, $on }) => ($on ? "none" : `inset 0 0 0 1px ${theme.lineStrong}`)};
  }
  .label {
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme, $active, $done }) => ($active ? theme.ink : $done ? theme.inkSoft : theme.inkMuted)};
  }
  &:hover .label {
    text-decoration: ${({ $done }) => ($done ? "underline" : "none")};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
    border-radius: 6px;
  }
`;

const Toggle = styled.div`
  display: flex;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  border: 0;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 7px;
  cursor: pointer;
  background: ${({ theme, $on }) => ($on ? theme.ink : "transparent")};
  color: ${({ theme, $on }) => ($on ? theme.surface : theme.inkSoft)};

  span {
    font-size: 11px;
    font-weight: 600;
    color: ${({ theme, $on }) => ($on ? theme.surface : theme.secondary)};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }
`;

const Head = styled.header`
  margin: 28px 0 32px;

  h1 {
    font-family: "Instrument Serif", Georgia, serif;
    font-weight: 400;
    font-size: 56px;
    line-height: 1;
    letter-spacing: -0.01em;
    margin: 0;
    color: ${({ theme }) => theme.ink};
  }
  h1 em {
    font-style: italic;
    color: ${({ theme }) => theme.accent};
  }
  p {
    margin: 12px 0 0;
    color: ${({ theme }) => theme.inkSoft};
    font-size: 15px;
    text-wrap: pretty;
    max-width: 60ch;
  }

  @media (max-width: 760px) {
    margin: 22px 0 24px;
    h1 {
      font-size: 38px;
    }
  }
`;

const BackLink = styled.button`
  display: block;
  background: none;
  border: 0;
  padding: 0;
  margin-bottom: 10px;
  font: inherit;
  font-size: 13px;
  color: ${({ theme }) => theme.accent};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

/* Content left, the order on the right; the pane sits beside the app sidebar, so the
   side column gives way earlier than it would on a full-width page. */
const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${({ $wide }) => ($wide ? "400px" : "340px")};
  gap: 28px;
  align-items: ${({ $fill }) => ($fill ? "stretch" : "start")};
  ${({ $fill }) => ($fill ? "flex: 1;" : "")}

  @media (max-width: 1280px) {
    grid-template-columns: minmax(0, 1fr) 300px;
    gap: 20px;
  }
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Main = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;

  /* Filling the pane: the last card runs to the bottom. */
  ${({ $fill }) =>
    $fill
      ? `@media (min-width: 901px) {
    & > *:last-child {
      flex: 1;
    }
  }`
      : ""}
`;

const Aside = styled.aside`
  min-width: 0;
  position: sticky;
  top: 24px;

  /* When the main column fills the pane, the order keeps its own height and stays in view. */
  ${({ $fill }) => ($fill ? "align-self: start;" : "")}

  @media (max-width: 900px) {
    position: static;
  }
`;

/* Every checkout view fills the pane top to bottom (MainContent pads 2.5rem, 4.75rem + 1.5rem on tablets). */
const FlowPage = styled(Page)`
  max-width: none;
  display: flex;
  flex-direction: column;
  min-height: calc(100dvh - 5rem);
  padding-bottom: 0;

  @media (max-width: 1024px) {
    min-height: calc(100dvh - 6.25rem);
  }
  @media (max-width: 900px) {
    min-height: 0;
    padding-bottom: 48px;
  }
`;

const Card = styled.section`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 14px;
  padding: 24px;

  @media (max-width: 450px) {
    padding: 18px;
  }
`;

const LinkButton = styled.button`
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.accent};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

/* Step 1: plans as radio cards, three abreast on the wide layout. */
const PlanGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;

  @media (min-width: 901px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

/* Under the plans: the seats card grows to the bottom of the pane, the rest sits beneath it. */
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;

  @media (min-width: 901px) {
    & > section:first-child {
      flex: 1;
    }
  }
`;

const PlanCard = styled.button`
  text-align: left;
  font: inherit;
  color: ${({ theme }) => theme.ink};
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme, $on }) => ($on ? theme.accent : theme.line)};
  box-shadow: ${({ theme, $on }) => ($on ? `0 0 0 3px ${theme.accentSoft}` : "none")};
  border-radius: 16px;
  padding: 24px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 18px;
  transition:
    box-shadow 0.15s,
    border-color 0.15s,
    transform 0.15s;

  &:hover {
    border-color: ${({ theme, $on }) => ($on ? theme.accent : theme.lineStrong)};
    transform: translateY(-1px);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }
  @media (min-width: 1280px) {
    padding: 28px;
  }
  .top {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .name {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 32px;
    line-height: 1;
  }
  .pill {
    font-size: 11px;
    font-weight: 500;
    color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.accentSoft};
    padding: 2px 8px;
    border-radius: 99px;
    white-space: nowrap;
  }
  .radio {
    margin-left: auto;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    box-sizing: border-box;
    border: ${({ theme, $on }) => ($on ? `5px solid ${theme.accent}` : `1.5px solid ${theme.lineStrong}`)};
    background: ${({ theme }) => theme.surface};
    flex: none;
  }
  .price {
    display: flex;
    align-items: baseline;
    gap: 6px;
    font-variant-numeric: tabular-nums;
  }
  .price b {
    font-size: 36px;
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: 1;
  }
  .price span {
    font-size: 13px;
    color: ${({ theme }) => theme.inkSoft};
  }
  .credits {
    display: inline-flex;
    align-items: center;
    margin-top: 10px;
    padding: 4px 10px;
    border-radius: 99px;
    font-size: 12.5px;
    font-weight: 500;
    color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.accentSoft};
  }
  hr {
    margin: 0;
    border: 0;
    height: 1px;
    background: ${({ theme }) => theme.lineSoft};
  }
  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 9px;
  }
  li {
    position: relative;
    padding-left: 26px;
    font-size: 13.5px;
    line-height: 1.45;
    color: ${({ theme }) => theme.ink2};
  }
  /* A soft disc with a check in the theme's accent, drawn with borders so it follows dark mode. */
  li::before {
    content: "";
    position: absolute;
    left: 0;
    top: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${({ theme }) => theme.accentSoft};
  }
  li::after {
    content: "";
    position: absolute;
    left: 4.5px;
    top: 6px;
    width: 6px;
    height: 3px;
    border-left: 1.8px solid ${({ theme }) => theme.accent};
    border-bottom: 1.8px solid ${({ theme }) => theme.accent};
    transform: rotate(-45deg);
  }
  /* Always in the flow (empty when no seat is on this plan) so the cards keep one height as the selection moves. */
  .count {
    font-size: 12px;
    line-height: 18px;
    min-height: 18px;
    color: ${({ theme }) => theme.inkSoft};
    margin-top: auto;
  }
`;

/* Seats: one row per person. */
const SeatsCard = styled(Card)`
  padding: 8px 24px 14px;
  display: flex;
  flex-direction: column;

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 14px 0 12px;
  }
  .head h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }
  .head p {
    margin: 2px 0 0;
    font-size: 13px;
    color: ${({ theme }) => theme.inkSoft};
  }
  .head .total {
    margin-left: auto;
    font-size: 13px;
    color: ${({ theme }) => theme.inkSoft};
    font-variant-numeric: tabular-nums;
  }
  .head .total b {
    color: ${({ theme }) => theme.ink};
    font-weight: 600;
  }
  .rows {
    display: flex;
    flex-direction: column;
  }
  .hint {
    font-size: 12.5px;
    color: ${({ theme }) => theme.inkSoft};
    padding: 12px 0 2px;
    margin-top: auto;
  }
  @media (max-width: 450px) {
    padding: 4px 18px 12px;
  }
`;

const AddSeat = styled.button`
  flex: none;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.accent};
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.lineStrong};
  border-radius: 99px;
  padding: 7px 14px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.bg2};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const SeatRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 14px 0;
  border-top: 1px solid ${({ theme }) => theme.lineSoft};

  .who {
    flex: 1 1 180px;
    min-width: 0;
  }
  .who b {
    display: block;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .who small {
    display: block;
    font-size: 12px;
    color: ${({ theme }) => theme.inkSoft};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .who input {
    width: 100%;
    box-sizing: border-box;
    font: inherit;
    font-size: 14px;
    border: 1px solid ${({ theme }) => theme.line};
    border-radius: 8px;
    padding: 7px 10px;
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.ink};
  }
  .who input:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.accent};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.accentSoft};
  }
  .who input[aria-invalid="true"] {
    border-color: ${({ theme }) => theme.danger};
  }
  .plan {
    font-size: 13px;
    color: ${({ theme }) => theme.ink2};
  }
  .price {
    width: 92px;
    text-align: right;
    font-size: 14px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .slot {
    width: 24px;
    display: flex;
    justify-content: center;
  }
  .remove {
    border: 0;
    background: none;
    color: ${({ theme }) => theme.inkMuted};
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    padding: 2px;
  }
  .remove:hover {
    color: ${({ theme }) => theme.ink};
  }
  /* A pickable row: the whole row selects, the radio shows it. */
  &[role="radio"] {
    cursor: pointer;
  }
  .pick {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    box-sizing: border-box;
    flex: none;
    background: ${({ theme }) => theme.surface};
    border: ${({ theme, $on }) => ($on ? `5px solid ${theme.accent}` : `1.5px solid ${theme.lineStrong}`)};
  }
`;

const Avatar = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ theme }) => theme.accentSoft};
  color: ${({ theme }) => theme.accent};
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 600;
  flex: none;
`;

const Segmented = styled.div`
  display: flex;
  background: ${({ theme }) => theme.bg2};
  border-radius: 8px;
  padding: 3px;
  gap: 2px;

  button {
    border: 0;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    padding: 6px 14px;
    border-radius: 6px;
    cursor: pointer;
    background: transparent;
    color: ${({ theme }) => theme.inkSoft};
  }
  button[aria-pressed="true"] {
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.ink};
    box-shadow: ${({ theme }) => theme.shadowSm || "0 1px 2px rgba(14, 23, 38, 0.08)"};
  }
`;

const EnterpriseStrip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 20px;
  border: 1px dashed ${({ theme }) => theme.lineStrong};
  border-radius: 14px;
  font-size: 13px;
  color: ${({ theme }) => theme.ink2};

  b {
    font-weight: 600;
    color: ${({ theme }) => theme.ink};
  }
`;

const CreditsPanel = styled(Card)`
  margin-top: 12px;

  p {
    margin: 0 0 18px;
    font-size: 14px;
    color: ${({ theme }) => theme.ink2};
    text-wrap: pretty;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 16px 28px;
  }
  .grid b {
    display: block;
    font-size: 13px;
    font-weight: 600;
  }
  .grid span {
    font-size: 13px;
    color: ${({ theme }) => theme.inkSoft};
  }
`;

const CREDIT_USES = [
  ["Candidate search", "Describe the role, get a ranked shortlist."],
  ["Similar candidates", "Find more like one strong profile."],
  ["Email lookup", "Verified work email, one flat price."],
  ["Job matching", "Candidates scored against your open roles."],
  ["Company tracking", "Careers pages watched, new roles each morning."],
  ["CV & spreadsheet import", "Resumes read into structured profiles."],
  ["Search assistant", "Tune the brief in chat before a search runs."],
  ["Outreach drafts", "A first email from profile and job, ready to send."],
];

/* Step 2: one add-on per card, copy left, counter right. */
const AddOn = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 18px;

  .row {
    display: flex;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
    align-items: flex-start;
  }
  .copy {
    flex: 1 1 280px;
  }
  .eyebrow {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.accent};
  }
  h2 {
    margin: 4px 0 0;
    font-family: "Instrument Serif", Georgia, serif;
    font-weight: 400;
    font-size: 30px;
    line-height: 1.1;
    color: ${({ theme }) => theme.ink};
  }
  .desc {
    margin: 6px 0 0;
    font-size: 14px;
    color: ${({ theme }) => theme.inkSoft};
    max-width: 420px;
    text-wrap: pretty;
  }
  .more {
    margin-top: 10px;
    font-size: 13px;
  }
  .controls {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }
  .cost {
    font-size: 13px;
    color: ${({ theme }) => theme.inkSoft};
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  .cost b {
    font-weight: 600;
    color: ${({ theme }) => theme.ink};
  }
`;

const Counter = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 10px;
  background: ${({ theme }) => theme.surface};

  button {
    width: 40px;
    height: 40px;
    border: 0;
    background: none;
    font: inherit;
    font-size: 18px;
    color: ${({ theme }) => theme.ink};
    cursor: pointer;
  }
  button:disabled {
    color: ${({ theme }) => theme.inkFaint};
    cursor: default;
  }
  output {
    min-width: 72px;
    text-align: center;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;

const Balance = styled.div`
  background: ${({ theme }) => theme.bg2};
  border-radius: 10px;
  padding: 14px 16px;

  .top {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    margin-bottom: 8px;
    font-variant-numeric: tabular-nums;
  }
  .top span {
    color: ${({ theme }) => theme.ink2};
  }
  .top b {
    font-weight: 600;
  }
  .bar {
    height: 6px;
    border-radius: 99px;
    background: ${({ theme }) => theme.lineSoft};
    display: flex;
    overflow: hidden;
  }
  .bar i {
    display: block;
    height: 100%;
    transition: width 200ms ease;
  }
  .bar .plan {
    background: ${({ theme }) => theme.accent};
  }
  .bar .extra {
    background: ${({ theme }) => theme.accent};
    opacity: 0.45;
  }
  .sub {
    font-size: 12px;
    color: ${({ theme }) => theme.inkSoft};
    margin-top: 8px;
  }
`;

/* Extra credits: pick an amount from tiles or dial one in; see the balance it leaves. */
const TOPUP_PRESETS = [5, 10, 25, 50]; // blocks

const Picker = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 18px;

  .head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    flex-wrap: wrap;
  }
  .head h2 {
    margin: 0;
    font-family: "Instrument Serif", Georgia, serif;
    font-weight: 400;
    font-size: 30px;
    line-height: 1.1;
  }
  .head span {
    font-size: 13px;
    color: ${({ theme }) => theme.inkSoft};
  }
  .tiles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
  }
  .custom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    padding-top: 16px;
    border-top: 1px solid ${({ theme }) => theme.lineSoft};
  }
  .custom .label {
    font-size: 14px;
    font-weight: 500;
  }
  .custom .label small {
    display: block;
    font-size: 12.5px;
    font-weight: 400;
    color: ${({ theme }) => theme.inkSoft};
    margin-top: 2px;
  }
  .custom .right {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
  }
  .custom .cost {
    font-size: 15px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    min-width: 64px;
    text-align: right;
  }
`;

const AmountTile = styled.button`
  font: inherit;
  text-align: left;
  color: ${({ theme }) => theme.ink};
  background: ${({ theme, $on }) => ($on ? theme.accentSoft : theme.surface)};
  border: 1px solid ${({ theme, $on }) => ($on ? theme.accent : theme.line)};
  box-shadow: ${({ theme, $on }) => ($on ? `0 0 0 3px ${theme.accentSoft}` : "none")};
  border-radius: 12px;
  padding: 16px 16px 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition:
    border-color 0.15s,
    box-shadow 0.15s,
    background 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.accent};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }
  .n {
    font-size: 22px;
    font-weight: 600;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
  }
  .n small {
    font-size: 12.5px;
    font-weight: 500;
    color: ${({ theme }) => theme.inkSoft};
    margin-left: 4px;
    letter-spacing: 0;
  }
  .usd {
    font-size: 13px;
    color: ${({ theme, $on }) => ($on ? theme.accent : theme.inkSoft)};
    font-weight: 500;
  }
  .tag {
    margin-top: 6px;
    align-self: flex-start;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.accent};
  }
`;

/* Where the credits land: one row per balance, now → after, the top-up as an accent chip. */
const Landing = styled(Card)`
  padding: 8px 24px 10px;

  .head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    padding: 14px 0 10px;
  }
  .head h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }
  .head span {
    font-size: 13px;
    color: ${({ theme }) => theme.inkSoft};
  }
  .row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(170px, auto);
    gap: 32px;
    align-items: center;
    padding: 26px 0;
    border-top: 1px solid ${({ theme }) => theme.lineSoft};
  }
  .who b {
    display: block;
    font-size: 15px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .who small {
    display: block;
    margin-top: 3px;
    font-size: 12.5px;
    color: ${({ theme }) => theme.inkSoft};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* The new balance, large, with where it came from in a quiet line beneath. */
  .move {
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .move .after {
    display: block;
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 34px;
    line-height: 1;
    letter-spacing: -0.01em;
  }
  .move .after small {
    font-family: inherit;
    font-size: 16px;
    color: ${({ theme }) => theme.inkSoft};
    margin-left: 5px;
  }

  .add {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  .add .chip {
    display: block;
    font-size: 15px;
    font-weight: 600;
    color: ${({ theme }) => theme.accent};
  }

  .foot {
    padding: 12px 0 2px;
    border-top: 1px solid ${({ theme }) => theme.lineSoft};
    font-size: 12.5px;
    color: ${({ theme }) => theme.inkSoft};
  }

  @media (max-width: 640px) {
    .row {
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 16px 20px;
      padding: 20px 0;
    }
    .move {
      grid-column: 1 / -1;
      text-align: left;
    }
    .move .after {
      font-size: 28px;
    }
  }
  @media (max-width: 450px) {
    padding: 4px 18px 10px;
  }
`;

/* Step 3: who's covered, invoice email, what happens next. */
const Covered = styled(Card)`
  padding: 8px 20px;

  .head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
  }
  .head h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }
`;

const Field = styled.label`
  display: block;

  .label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 6px;
  }
  input {
    width: 100%;
    max-width: 420px;
    box-sizing: border-box;
    font: inherit;
    font-size: 14px;
    border: 1px solid ${({ theme }) => theme.line};
    border-radius: 10px;
    padding: 10px 12px;
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.ink};
  }
  input:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.accent};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.accentSoft};
  }
  input[aria-invalid="true"] {
    border-color: ${({ theme }) => theme.danger};
  }
  small {
    display: block;
    font-size: 12px;
    color: ${({ theme }) => theme.inkSoft};
    margin-top: 6px;
  }
`;

/* The promo code: an input with its Apply button on one line, or the applied code as a tag. */
const PromoRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
  max-width: 420px;

  input {
    flex: 1;
    min-width: 0;
    text-transform: uppercase;
  }
  input::placeholder {
    text-transform: none;
  }
  button {
    flex: none;
    height: auto;
    padding: 0 16px;
    border-radius: 10px;
  }
`;

const PromoTag = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  max-width: 420px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.bg2};
  font-size: 14px;

  b {
    font-weight: 600;
    letter-spacing: 0.02em;
  }
  .off {
    color: ${({ theme }) => theme.success};
    font-weight: 500;
  }
  button {
    margin-left: auto;
  }
`;

const PromoError = styled.small`
  && {
    color: ${({ theme }) => theme.danger};
  }
`;

const Note = styled.p`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin: 0;
  padding: 4px 4px 0;
  font-size: 13px;
  color: ${({ theme }) => theme.inkSoft};
  text-wrap: pretty;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.success};
    flex: none;
    margin-top: 7px;
  }
`;

/* The order panel: lines, what is due, what recurs, the one action. */
const OrderCard = styled.section`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 16px;
  padding: 24px;

  .head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
  }
  .head h2 {
    margin: 0;
    font-family: "Instrument Serif", Georgia, serif;
    font-weight: 400;
    font-size: 30px;
    line-height: 1;
  }
  .head span {
    font-size: 12px;
    color: ${({ theme }) => theme.inkSoft};
    text-align: right;
  }
  .lines {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 20px;
  }
  .line {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-variant-numeric: tabular-nums;
  }
  .line .l {
    font-size: 14px;
  }
  .line small {
    display: block;
    font-size: 12px;
    color: ${({ theme }) => theme.inkSoft};
  }
  .line b {
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
  }
  hr {
    margin: 20px 0 16px;
    border: 0;
    height: 1px;
    background: ${({ theme }) => theme.lineSoft};
  }
  .due {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
  }
  .due span {
    font-size: 14px;
    font-weight: 500;
  }
  .due strong {
    font-family: "Instrument Serif", Georgia, serif;
    font-weight: 400;
    font-size: 46px;
    line-height: 0.9;
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .due strong small {
    font-family: inherit;
    font-size: 20px;
    color: ${({ theme }) => theme.inkSoft};
    margin-left: 4px;
  }
  .then {
    margin-top: 14px;
    background: ${({ theme }) => theme.bg2};
    border-radius: 10px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .then div {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
  }
  .then div span {
    color: ${({ theme }) => theme.ink2};
  }
  .then div b {
    font-weight: 500;
    white-space: nowrap;
  }
  .then div b.credits {
    color: ${({ theme }) => theme.accent};
  }
  .then small {
    font-size: 12px;
    color: ${({ theme }) => theme.inkSoft};
  }
  .note {
    text-align: center;
    font-size: 12px;
    color: ${({ theme }) => theme.inkMuted};
    margin-top: 10px;
  }

  @media (max-width: 1280px) {
    padding: 20px;
    .due strong {
      font-size: 40px;
    }
  }
  @media (max-width: 450px) {
    padding: 20px 18px;
    .due strong {
      font-size: 38px;
    }
  }
`;

const Agree = styled.label`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-top: 18px;
  font-size: 13px;
  color: ${({ theme }) => theme.ink2};
  cursor: pointer;

  input {
    margin: 2px 0 0;
    width: 16px;
    height: 16px;
    /* The phone-wide 44px input min-height would stretch the checkbox off its text line. */
    min-height: 0;
    flex-shrink: 0;
    accent-color: ${({ theme }) => theme.accent};
    cursor: pointer;
  }
  a {
    color: ${({ theme }) => theme.accent};
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
`;

const Cta = styled.button`
  margin-top: 18px;
  width: 100%;
  height: 48px;
  border: 0;
  border-radius: 10px;
  background: ${({ theme }) => theme.accent};
  color: ${({ theme }) => theme.onFill};
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.accentHover};
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
    background: ${({ theme }) => theme.accent};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }
`;

const ErrorText = styled.p`
  margin: 12px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.danger};
`;

const Button = styled.button`
  height: 42px;
  padding: 0 20px;
  border-radius: 999px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${({ theme, $primary }) => ($primary ? theme.accent : theme.lineStrong)};
  background: ${({ theme, $primary }) => ($primary ? theme.accent : "transparent")};
  color: ${({ theme, $primary }) => ($primary ? theme.onFill : theme.ink)};

  &:hover {
    background: ${({ theme, $primary }) => ($primary ? theme.accentHover : theme.bg2)};
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

/* Back from payment: the whole pane, hero on top filling the height, next steps below. */
const DonePage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-height: calc(100dvh - 5rem);
`;

const DoneHero = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 56px 56px 52px;
  border-radius: 28px;
  background:
    radial-gradient(900px 420px at 88% -20%, ${({ theme }) => theme.accentSoft}, transparent 65%),
    ${({ theme }) => theme.bg2};

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    width: fit-content;
    height: 32px;
    padding: 0 14px 0 10px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.02em;
    background: ${({ theme, $tone }) => ($tone === "ok" ? theme.successSoft : theme.surface)};
    color: ${({ theme, $tone }) => ($tone === "ok" ? theme.success : theme.inkSoft)};
    border: 1px solid ${({ theme, $tone }) => ($tone === "ok" ? "transparent" : theme.line)};
  }
  .eyebrow i {
    display: grid;
    place-items: center;
    width: 18px;
    height: 18px;
    border-radius: 99px;
    background: ${({ theme }) => theme.success};
    color: ${({ theme }) => theme.onFill};
  }
  h1 {
    margin: 26px 0 0;
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 84px;
    font-weight: 400;
    line-height: 0.98;
    letter-spacing: -0.02em;
    color: ${({ theme }) => theme.ink};
    max-width: 14ch;
  }
  h1 em {
    font-style: italic;
    color: ${({ theme }) => theme.accent};
  }
  .lede {
    margin: 22px 0 0;
    max-width: 58ch;
    font-size: 17px;
    line-height: 1.55;
    color: ${({ theme }) => theme.inkSoft};
  }

  @media (max-width: 1100px) {
    h1 {
      font-size: 64px;
    }
  }
  @media (max-width: 760px) {
    padding: 30px 22px 28px;
    border-radius: 20px;
    h1 {
      font-size: 46px;
    }
    .lede {
      font-size: 15.5px;
    }
  }
`;

const Stats = styled.div`
  margin-top: 40px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Stat = styled.div`
  padding: 22px 24px 24px;
  border-radius: 18px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};

  .label {
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.inkMuted};
  }
  .value {
    margin-top: 10px;
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 40px;
    line-height: 1;
    letter-spacing: -0.015em;
    color: ${({ theme }) => theme.ink};
    min-height: 40px;
  }
  .value.pending {
    width: 60%;
    height: 34px;
    margin-top: 13px;
    border-radius: 8px;
    background: ${({ theme }) => theme.lineSoft};
    animation: ck-pulse 1.4s ease-in-out infinite;
  }
  .hint {
    margin-top: 8px;
    font-size: 13.5px;
    line-height: 1.45;
    color: ${({ theme }) => theme.inkSoft};
  }

  @media (max-width: 760px) {
    padding: 18px 18px 20px;
    .value {
      font-size: 32px;
    }
  }
`;

const DoneActions = styled.div`
  margin-top: 34px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const NextSteps = styled.section`
  padding: 36px 40px 40px;
  border-radius: 28px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};

  .head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px 24px;
  }
  h2 {
    margin: 0;
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 36px;
    font-weight: 400;
    line-height: 1;
    letter-spacing: -0.015em;
    color: ${({ theme }) => theme.ink};
  }
  h2 em {
    font-style: italic;
    color: ${({ theme }) => theme.accent};
  }
  .sub {
    margin: 0;
    font-size: 15px;
    color: ${({ theme }) => theme.inkMuted};
  }

  @media (max-width: 760px) {
    padding: 24px 20px 24px;
    border-radius: 20px;
    h2 {
      font-size: 30px;
    }
  }
`;

const StepGrid = styled.div`
  margin-top: 26px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Step = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 0;
  padding: 22px 22px 20px;
  border-radius: 18px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.bg2};
  font: inherit;
  color: ${({ theme }) => theme.ink};
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    transform 0.15s ease;

  .icon {
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: ${({ theme }) => theme.surface};
    border: 1px solid ${({ theme }) => theme.line};
    color: ${({ theme }) => theme.accent};
  }
  .n {
    margin-top: 18px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.inkMuted};
  }
  h3 {
    margin: 6px 0 0;
    font-size: 17px;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
  p {
    margin: 8px 0 0;
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => theme.inkSoft};
  }
  .go {
    margin-top: auto;
    padding-top: 16px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.accent};
  }

  &:hover {
    border-color: ${({ theme }) => theme.accent};
    transform: translateY(-2px);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }
`;

const FIRST_STEPS = [
  {
    icon: FiBriefcase,
    title: "Track a company",
    text: "Add a firm you recruit for. New roles from its careers page land in Jobs each morning.",
    cta: "Go to Jobs",
    to: "/jobs",
  },
  {
    icon: FiUserPlus,
    title: "Run a candidate search",
    text: "Describe the role in a sentence or two and get a ranked shortlist of people.",
    cta: "Find candidates",
    to: "/find-candidates",
  },
  {
    icon: FiAtSign,
    title: "Look up a work email",
    text: "Pick anyone on a shortlist and get a verified business address for a flat 12 credits.",
    cta: "Open Outreach",
    to: "/outreach",
  },
  {
    icon: FiSend,
    title: "Draft the first message",
    text: "A first email written from the profile and the job, ready for you to edit and send.",
    cta: "Start drafting",
    to: "/outreach",
  },
];

/* What a change to a live plan does, line by line. */
/* Reviewing a plan change: the plan now beside the plan after, then what happens and when. */
const Compare = styled.div`
  display: grid;
  grid-template-columns: ${({ $three }) => ($three ? "minmax(0, 1fr) auto minmax(0, 1fr) auto minmax(0, 1fr)" : "minmax(0, 1fr) auto minmax(0, 1fr)")};
  gap: 14px;
  align-items: stretch;
  margin-top: 16px;

  .col {
    min-width: 0;
    padding: 16px 18px;
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.line};
    background: ${({ theme }) => theme.bg2};
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .col.next {
    background: ${({ theme }) => theme.accentSoft};
    border-color: transparent;
  }
  .eyebrow {
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.inkSoft};
  }
  .col.next .eyebrow {
    color: ${({ theme }) => theme.accent};
  }
  .tier {
    font-family: "Instrument Serif", Georgia, serif;
    font-weight: 400;
    font-size: 26px;
    line-height: 1.1;
    letter-spacing: -0.01em;
    overflow-wrap: anywhere;
  }
  .meta {
    font-size: 13px;
    color: ${({ theme }) => theme.inkSoft};
  }
  .price {
    margin-top: auto;
    padding-top: 10px;
    font-size: 15px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .price small {
    font-weight: 500;
    color: ${({ theme }) => theme.inkSoft};
  }
  .credits {
    font-size: 13px;
    color: ${({ theme }) => theme.ink2};
  }
  .delta {
    align-self: flex-start;
    margin-top: 2px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.surface};
  }
  .arrow {
    align-self: center;
    color: ${({ theme }) => theme.inkSoft};
    display: flex;
  }

  /* Three tiles need the room; otherwise only phones stack them. */
  @media (max-width: ${({ $three }) => ($three ? "900px" : "560px")}) {
    grid-template-columns: 1fr;
    gap: 8px;
    .arrow {
      justify-content: center;
      transform: rotate(90deg);
    }
    .tier {
      font-size: 22px;
    }
  }
`;

const Timeline = styled.ol`
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
  display: grid;

  li {
    position: relative;
    display: grid;
    grid-template-columns: 120px 1fr;
    gap: 16px;
    padding: 0 0 18px 0;
  }
  li:last-child {
    padding-bottom: 0;
  }
  /* The rail: a dot per step, joined by a hairline. */
  li::before {
    content: "";
    position: absolute;
    left: 120px;
    top: 7px;
    bottom: -7px;
    width: 1px;
    margin-left: -8px;
    background: ${({ theme }) => theme.lineSoft};
  }
  li:last-child::before {
    display: none;
  }
  .dot {
    position: absolute;
    left: 120px;
    top: 4px;
    width: 9px;
    height: 9px;
    margin-left: -12px;
    border-radius: 50%;
    background: ${({ theme }) => theme.inkSoft};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.surface};
  }
  li.now .dot {
    background: ${({ theme }) => theme.accent};
  }
  li.ok .dot {
    background: ${({ theme }) => theme.success};
  }
  .when {
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.inkSoft};
    padding-right: 20px;
    line-height: 1.4;
  }
  li.now .when {
    color: ${({ theme }) => theme.accent};
  }
  .what {
    min-width: 0;
    padding-left: 10px;
  }
  .what b {
    display: block;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.4;
    font-variant-numeric: tabular-nums;
  }
  .what p {
    margin: 3px 0 0;
    font-size: 13.5px;
    line-height: 1.5;
    color: ${({ theme }) => theme.inkSoft};
    text-wrap: pretty;
  }
  .what .bar {
    display: block;
    margin-top: 10px;
    height: 6px;
    border-radius: 3px;
    background: ${({ theme }) => theme.lineSoft};
    overflow: hidden;
    max-width: 320px;
  }
  .what .bar i {
    display: block;
    height: 6px;
    border-radius: 3px;
    background: ${({ theme }) => theme.accent};
  }

  @media (max-width: 560px) {
    li {
      grid-template-columns: 1fr;
      gap: 2px;
      padding-left: 22px;
    }
    li::before {
      left: 4px;
      margin-left: 0;
      top: 16px;
      bottom: -4px;
    }
    .dot {
      left: 0;
      margin-left: 0;
      top: 5px;
    }
    .what {
      padding-left: 0;
    }
  }
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
`;

/* ------------------------------------------------------------ components */

/* The numbered steps across the top; finished ones are links back. */
function StepBar({ steps, step, onGo, right }) {
  return (
    <TopBar>
      <Steps aria-label="Checkout steps">
        {steps.map((label, i) => {
          const n = i + 1;
          const done = n < step;
          return (
            <li key={label}>
              <StepButton type="button" $on={n <= step} $active={n === step} $done={done} aria-current={n === step ? "step" : undefined} disabled={!done} onClick={() => done && onGo(n)}>
                <span className="dot">{done ? <FiCheck size={12} strokeWidth={3} /> : n}</span>
                <span className="label">{label}</span>
              </StepButton>
              {n < steps.length && <span className="rule" aria-hidden="true" />}
            </li>
          );
        })}
      </Steps>
      {right}
    </TopBar>
  );
}

/* The side panel. `then` is what recurs; `credits` the monthly allowance; children sit
   between the totals and the button (the terms checkbox on the pay step). */
function Order({ billingLabel, lines, dueLabel = "Due today", due, dueUnit, then, credits, children, cta, note, error }) {
  return (
    <OrderCard aria-label="Your order">
      <div className="head">
        <h2>Your order</h2>
        {billingLabel && <span>{billingLabel}</span>}
      </div>
      <div className="lines">
        {lines.map((l) => (
          <div className="line" key={l.key || l.label}>
            <div className="l">
              {l.label}
              {l.sub && <small>{l.sub}</small>}
            </div>
            <b>{l.amount}</b>
          </div>
        ))}
      </div>
      <hr />
      <div className="due">
        <span>{dueLabel}</span>
        <strong>
          {due}
          {dueUnit && <small>{dueUnit}</small>}
        </strong>
      </div>
      {(then || credits) && (
        <div className="then">
          {then && (
            <div>
              <span>{then.label}</span>
              <b>{then.amount}</b>
            </div>
          )}
          {credits && (
            <div>
              <span>Credits each month</span>
              <b className="credits">{credits}</b>
            </div>
          )}
          {then?.sub && <small>{then.sub}</small>}
        </div>
      )}
      {children}
      {cta && (
        <Cta type="button" onClick={cta.onClick} disabled={cta.disabled}>
          {cta.pending && <span style={spin} />}
          {cta.label}
        </Cta>
      )}
      {error && <ErrorText role="alert">{error}</ErrorText>}
      {note && <div className="note">{note}</div>}
    </OrderCard>
  );
}

function Terms({ checked, onChange }) {
  return (
    <Agree>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        I agree to the{" "}
        <a href="/terms" target="_blank" rel="noreferrer">
          Terms of Service
        </a>{" "}
        and{" "}
        <a href="/privacy-policy" target="_blank" rel="noreferrer">
          Privacy Policy
        </a>
        .
      </span>
    </Agree>
  );
}

function PlanToggle({ yearly, onChange }) {
  return (
    <Toggle role="group" aria-label="Billing period">
      <ToggleButton type="button" $on={!yearly} aria-pressed={!yearly} onClick={() => onChange(false)}>
        Monthly
      </ToggleButton>
      <ToggleButton type="button" $on={yearly} aria-pressed={yearly} onClick={() => onChange(true)}>
        Yearly <span>−{Math.round(ANNUAL_DISCOUNT * 100)}%</span>
      </ToggleButton>
    </Toggle>
  );
}

// Where invoices go: the saved billing email, else the login email, editable for this order.
function useInvoiceEmail(user) {
  const [email, setEmail] = React.useState(() => user?.email || "");
  const [loaded, setLoaded] = React.useState(() => Boolean(user?.email));
  React.useEffect(() => {
    let alive = true;
    fetchWithAuth("/api/billing/contact")
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (alive && body?.email) setEmail((cur) => (!cur.trim() || cur.trim() === (body.loginEmail || "") ? body.email : cur));
      })
      .catch(() => {})
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, []);
  React.useEffect(() => {
    if (user?.email) setEmail((cur) => cur.trim() || user.email);
  }, [user?.email]);
  return { email, setEmail, loaded, ok: EMAIL_RE.test(email.trim()) };
}

// Hands the browser to Airwallex once the server has opened a checkout; only to its own pages.
// A promo code on the Pay step. `applied` is what the server confirmed for the cart as it stands;
// the cart changing re-checks it, and the code is dropped (with the reason shown) when it no longer fits.
function usePromo(startCode, order, enabled) {
  const [text, setText] = React.useState(startCode);
  const [applied, setApplied] = React.useState(null); // { code, amountOff, label }
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const orderKey = JSON.stringify(order);
  const check = React.useCallback(
    async (code) => {
      const clean = String(code || "").trim();
      if (!clean) return;
      setBusy(true);
      try {
        const res = await fetchWithAuth("/api/billing/promo/check", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: clean, ...JSON.parse(orderKey) }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || "That code isn't valid.");
        setApplied({ code: data.code, amountOff: Number(data.amountOff) || 0, label: data.label || "" });
        setText(data.code);
        setError("");
      } catch (err) {
        setApplied(null);
        setError(errorText(err, "That code isn't valid."));
      } finally {
        setBusy(false);
      }
    },
    [orderKey],
  );
  // The code from the URL (back from the payment page) applies itself; a cart change re-checks the
  // applied one. The ref keeps the applied code out of the effect's deps, so applying never re-checks.
  const appliedRef = React.useRef("");
  appliedRef.current = applied?.code || "";
  const first = React.useRef(Boolean(startCode));
  React.useEffect(() => {
    if (!enabled) return;
    if (first.current) {
      first.current = false;
      check(startCode);
    } else if (appliedRef.current) {
      check(appliedRef.current);
    }
  }, [check, enabled, startCode]);
  const clear = () => {
    setApplied(null);
    setText("");
    setError("");
  };
  return { text, setText, applied, busy, error, apply: () => check(text), clear };
}

function useHostedCheckout() {
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState("");
  // Back from the Airwallex page restores this page from the bfcache with the redirect still pending; re-enable Pay.
  React.useEffect(() => {
    const reset = () => setPending(false);
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);
  const start = async (path, body) => {
    if (pending) return;
    setPending(true);
    setError("");
    try {
      const res = await fetchWithAuth(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      const dest = res.ok && data.url ? checkoutDestination(data.url) : null;
      if (!dest) throw new Error(data?.error || "We couldn't start checkout. Please try again.");
      window.location.href = dest;
    } catch (err) {
      setError(errorText(err, "We couldn't start checkout. Please try again."));
      setPending(false);
    }
  };
  return { pending, error, setError, start };
}

// The people in the seats (names, invites), for a live plan with team seats on.
function useTeam(enabled) {
  const [team, setTeam] = React.useState(null);
  React.useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    fetchWithAuth("/api/team")
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => alive && body && setTeam(body))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [enabled]);
  return team;
}

/* Buying a plan (three steps) or changing a live one (two). `live` is the billing status of
   a plan that exists; the seats then start from what the account has. */
function PlanFlow({ seatSpec, startYearly, startBlocks, startFirms, teamOn, live }) {
  const q = useQuery();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { status, features: planFeatures } = usePlan();
  const isLive = Boolean(live);
  const steps = isLive ? ["Plan", "Review"] : ["Plan", "Add-ons", "Pay"];
  const step = Math.min(steps.length, Math.max(1, Number(q.get("step")) || 1));
  const member = status?.role === "member";
  const inhouse = isInhouse(planFeatures);
  const team = useTeam(isLive && teamOn);
  const [yearly, setYearly] = React.useState(isLive ? live.cycle === "yearly" : startYearly);
  const [seats, setSeats] = React.useState(() => {
    if (isLive) {
      const own = (live.seatList || []).length ? live.seatList : [{ id: null, plan: live.plan }];
      return own.map((x, i) => ({ key: x.id || `me-${i}`, id: x.id || null, plan: x.plan, email: "", userId: x.userId || null }));
    }
    let emails = [];
    try {
      const saved = JSON.parse(sessionStorage.getItem(EMAILS_KEY) || "[]");
      if (Array.isArray(saved) && saved.length === seatSpec.length) emails = saved;
    } catch {
      emails = [];
    }
    return seatSpec.map((s, i) => ({ key: s.id || `new-${i}`, id: s.id, plan: s.plan, email: i === 0 ? "" : String(emails[i] || ""), userId: null }));
  });
  const [blocks, setBlocks] = React.useState(startBlocks);
  const [firms, setFirms] = React.useState(inhouse ? 0 : startFirms);
  const [showCredits, setShowCredits] = React.useState(false);
  const [demoOpen, setDemoOpen] = React.useState(false);
  const [agreed, setAgreed] = React.useState(false);
  const [touched, setTouched] = React.useState(false);
  const invoice = useInvoiceEmail(user);
  const hosted = useHostedCheckout();
  const promo = usePromo(
    q.get("promo") || "",
    { cycle: yearly ? "yearly" : "monthly", seatPlans: seats.map((s) => s.plan), blocks, firms },
    !isLive && !member,
  );
  const multi = seats.length > 1;

  // Who sits where: the buyer first, then colleagues by seat (names from the team list).
  const isMe = (s, i) => (s.userId && user?.sub ? s.userId === user.sub : i === 0);
  const teamSeat = (id) => (id ? team?.seatList?.find((x) => x.id === id) : null);
  const holderName = (id) => {
    const t = teamSeat(id);
    return t?.holder?.name || t?.holder?.email || t?.invite?.email || "";
  };

  const tiers = tiersOf(seats);
  const seatsToday = tiers.reduce((n, [p, count]) => n + perSeatCharge(p, yearly) * count, 0);
  const seatsMonthly = tiers.reduce((n, [p, count]) => n + p.monthly * count, 0);
  const planCredits = seats.reduce((n, s) => n + planOf(s.plan).credits, 0);
  const extraCredits = blocks * TOPUP_BLOCK_CREDITS;
  const extraCost = blocks * TOPUP_BLOCK_USD;
  const firmsSetup = firms * ADDON_SETUP_USD;
  const firmsMonthly = firms * ADDON_MONTHLY_USD;
  const promoOff = promo.applied ? Math.min(promo.applied.amountOff, seatsToday + extraCost + firmsSetup + firmsMonthly) : 0;
  const due = Math.max(0, seatsToday + extraCost + firmsSetup + firmsMonthly - promoOff);
  const recurring = yearly ? seatsToday : seatsMonthly + firmsMonthly;
  const emailBad = seats.some((s) => !s.id && s.email.trim() && !EMAIL_RE.test(s.email.trim()));
  // A live plan: what it costs now, and whether anything here differs from it.
  const liveYearly = live?.cycle === "yearly";
  const liveAmount = isLive ? (live.seatList || []).reduce((n, x) => n + perSeatCharge(planOf(x.plan), liveYearly), 0) : 0;
  const unchanged =
    isLive &&
    liveYearly === yearly &&
    seats.length === (live.seatList || []).length &&
    seats.every((s, i) => s.id && live.seatList[i]?.id === s.id && live.seatList[i]?.plan === s.plan);

  const url = (n) =>
    `/checkout?step=${n}&cycle=${yearly ? "yearly" : "monthly"}&seats=${encodeURIComponent(seats.map((s) => `${s.id || "new"}:${s.plan}`).join(","))}&blocks=${blocks}&firms=${firms}${promo.applied ? `&promo=${encodeURIComponent(promo.applied.code)}` : ""}`;
  const go = (n) => {
    try {
      sessionStorage.setItem(EMAILS_KEY, JSON.stringify(seats.map((s) => s.email)));
    } catch {
      // storage is a convenience only
    }
    navigate(url(n));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const setSeat = (key, patch) => setSeats((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addSeat = () => setSeats((rows) => (rows.length >= MAX_SEATS ? rows : [...rows, { key: `new-${Date.now()}`, id: null, plan: rows[0].plan, email: "", userId: null }]));

  const pay = async () => {
    if (!invoice.ok) {
      setTouched(true);
      hosted.setError("Please enter a valid email for invoices.");
      return;
    }
    if (!agreed) {
      hosted.setError("Please agree to the terms and privacy policy to continue.");
      return;
    }
    try {
      sessionStorage.setItem(EMAILS_KEY, JSON.stringify(seats.map((s) => s.email)));
    } catch {
      // storage is a convenience only
    }
    // One hosted checkout for the plan, extra credits and tracked companies together.
    await hosted.start("/api/billing/checkout", {
      cycle: yearly ? "yearly" : "monthly",
      seatPlans: seats.map((s) => s.plan),
      ...(teamOn ? { seatEmails: seats.map((s, i) => (i === 0 ? "" : s.email.trim().toLowerCase())) } : {}),
      blocks,
      firms,
      billingEmail: invoice.email.trim(),
      ...(promo.applied ? { promo: promo.applied.code } : {}),
      agreed,
    });
  };

  const onNext = () => {
    if (step === 1 && emailBad) {
      setTouched(true);
      return;
    }
    if (step < steps.length) return go(step + 1);
    pay();
  };

  const stepBar = <StepBar steps={steps} step={step} onGo={go} right={step === 1 ? <PlanToggle yearly={yearly} onChange={setYearly} /> : null} />;

  // Reviewing a change to a live plan has its own card and totals.
  if (isLive && step === 2) {
    return (
      <ChangeView
        seatSpec={seats}
        cycle={yearly ? "yearly" : "monthly"}
        onBack={() => go(1)}
        top={stepBar}
      />
    );
  }

  const lines = tiers.map(([p, count]) => ({
    key: p.id,
    label: `${count} × ${p.name}`,
    sub: `${plural(count, "seat")}${yearly ? " · billed yearly" : ""}`,
    amount: usd(perSeatCharge(p, yearly) * count),
  }));
  if (blocks > 0) lines.push({ key: "extra", label: "Extra credits", sub: `${formatCredits(extraCredits)} one-off`, amount: usd(extraCost) });
  if (firms > 0) lines.push({ key: "firms", label: "Tracked companies", sub: `${plural(firms, "firm")} · incl. ${usd(firmsSetup)} setup`, amount: usd(firmsSetup + firmsMonthly) });
  if (promoOff > 0) lines.push({ key: "promo", label: "Promo code", sub: `${promo.applied.code} · ${promo.applied.label}, first payment`, amount: `−${usd(promoOff)}` });

  const ctaLabel = hosted.pending
    ? "Opening secure checkout…"
    : member
      ? "Managed by your admin"
      : isLive
        ? unchanged
          ? "Current plan"
          : "Review change"
        : step === 1
          ? "Continue"
          : step === 2
            ? blocks || firms
              ? "Continue to payment"
              : "Skip, go to payment"
            : `Pay ${usd(due)}`;
  const ctaDisabled = member || hosted.pending || (isLive && unchanged) || (step === 3 && (!agreed || !invoice.ok || promo.busy));
  const ctaNote = member
    ? "Only your account admin can change the plan."
    : isLive
      ? "Nothing changes until you confirm"
      : step === 3
        ? "Secured by Airwallex"
        : step === 1
          ? "Add-ons are optional"
          : "Nothing is charged yet";
  const seatTitle = (s, i) => {
    if (isMe(s, i)) return "You";
    if (s.id) return holderName(s.id) || (teamSeat(s.id)?.invite ? "Invited" : "Empty seat");
    return s.email.trim() || (isLive ? "New seat" : `Seat ${i + 1}`);
  };
  const seatSub = (s, i) => {
    if (isMe(s, i)) return user?.email;
    if (s.id) {
      const t = teamSeat(s.id);
      if (t?.holder?.name && t?.holder?.email) return t.holder.email;
      if (t?.invite) return `Invite out to ${t.invite.email}`;
      return "Invite someone from Settings → Team";
    }
    if (isLive) return "Invite someone from Settings → Team after paying";
    return s.email.trim() ? "Invite sent after payment" : "Add their email later in Settings";
  };
  const seatInitials = (s, i) => {
    if (isMe(s, i)) return initialsOf(user?.name, user?.email);
    const t = s.id ? teamSeat(s.id) : null;
    const name = t?.holder?.name || t?.holder?.email || t?.invite?.email || s.email.trim();
    return name ? initialsOf(name, name) : String(i + 1);
  };
  // A seat with someone in it (or an invite out) can change tier but not be removed.
  const seatLocked = (s, i) => isMe(s, i) || Boolean(s.id && (teamSeat(s.id)?.holder || teamSeat(s.id)?.invite));

  return (
    <FlowPage>
      <style>{`@keyframes ck-spin{to{transform:rotate(360deg)}}`}</style>
      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
      {stepBar}

      <Head>
        {step > 1 && (
          <BackLink type="button" onClick={() => go(step - 1)}>
            ← Back
          </BackLink>
        )}
        {step === 1 && isLive && (
          <>
            <h1>
              Change your <em>plan.</em>
            </h1>
            <p>Upgrades start right away on your saved card. Moving a seat down, dropping a seat or switching how you're billed starts on your renewal date.</p>
          </>
        )}
        {step === 1 && !isLive && (
          <>
            <h1>
              Pick your <em>plan.</em>
            </h1>
            <p>
              {status?.status === "TRIAL_ENDED" || status?.status === "TRIAL_USED" ? "Your trial has ended. " : ""}
              Choose a plan to keep searching. Change or cancel any time.
            </p>
          </>
        )}
        {step === 2 && (
          <>
            <h1>
              Add <em>ons.</em>
            </h1>
            <p>Optional. You can add these from Settings later.</p>
          </>
        )}
        {step === 3 && (
          <>
            <h1>
              Review and <em>pay.</em>
            </h1>
            <p>Card details are taken on a secure page next.</p>
          </>
        )}
      </Head>

      <Layout $wide $fill>
        <Main $fill>
          {step === 1 && (
            <>
              <PlanGrid role="radiogroup" aria-label="Plan">
                {paidPlans.map((p) => {
                  const on = seats.every((s) => s.plan === p.id);
                  const count = seats.filter((s) => s.plan === p.id).length;
                  const features = planFeatureList(p, planFeatures?.edition);
                  const intro = p.intro ? p.intro.replace(/,?\s*plus$/i, "") : null;
                  const current = isLive && live.plan === p.id;
                  return (
                    <PlanCard key={p.id} type="button" role="radio" aria-checked={on} $on={on} onClick={() => setSeats((rows) => rows.map((r) => ({ ...r, plan: p.id })))}>
                      <div className="top">
                        <span className="name">{p.name}</span>
                        {current ? <span className="pill">Your plan</span> : p.featured ? <span className="pill">Popular</span> : null}
                        <span className="radio" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="price">
                          <b>{usd(perSeatMonth(p, yearly))}</b>
                          <span>/ seat / month</span>
                        </div>
                        <div className="credits">{formatCredits(p.credits)} credits a month</div>
                      </div>
                      <hr />
                      <ul>
                        {intro && <li>{intro}</li>}
                        {features.map((f) => (
                          <li key={f}>{f}</li>
                        ))}
                      </ul>
                      {multi && <div className="count">{count > 0 ? `${plural(count, "seat")} on this plan` : ""}</div>}
                    </PlanCard>
                  );
                })}
              </PlanGrid>

              <Stack>
              {teamOn && (
                <SeatsCard aria-labelledby="seats-title">
                  <div className="head">
                    <div>
                      <h2 id="seats-title">Seats</h2>
                      <p>One per person. Each gets their own login and monthly credits.</p>
                    </div>
                    <span className="total">
                      <b>{plural(seats.length, "seat")}</b> · {usd(tiers.reduce((n, [p, c]) => n + perSeatMonth(p, yearly) * c, 0))}/mo · {formatCredits(planCredits)} credits a month
                    </span>
                    <AddSeat type="button" onClick={addSeat} disabled={seats.length >= MAX_SEATS || member}>
                      + Add teammate
                    </AddSeat>
                  </div>
                  <div className="rows">
                  {seats.map((s, i) => {
                    const p = planOf(s.plan);
                    const bad = touched && !isMe(s, i) && !s.id && s.email.trim() && !EMAIL_RE.test(s.email.trim());
                    return (
                      <SeatRow key={s.key}>
                        <Avatar aria-hidden="true">{seatInitials(s, i)}</Avatar>
                        <div className="who">
                          {!isLive && !isMe(s, i) ? (
                            <input
                              type="email"
                              value={s.email}
                              onChange={(e) => setSeat(s.key, { email: e.target.value })}
                              placeholder="teammate@company.com"
                              aria-label={`Email for seat ${i + 1}`}
                              aria-invalid={bad ? "true" : undefined}
                              autoComplete="off"
                            />
                          ) : (
                            <>
                              <b>{seatTitle(s, i)}</b>
                              <small>{seatSub(s, i)}</small>
                            </>
                          )}
                        </div>
                        {multi ? (
                          <Segmented role="group" aria-label={`Plan for seat ${i + 1}`}>
                            {paidPlans.map((o) => (
                              <button key={o.id} type="button" aria-pressed={s.plan === o.id} onClick={() => setSeat(s.key, { plan: o.id })}>
                                {o.name}
                              </button>
                            ))}
                          </Segmented>
                        ) : (
                          <span className="plan">{p.name}</span>
                        )}
                        <span className="price">{usd(perSeatMonth(p, yearly))}/mo</span>
                        <span className="slot">
                          {!seatLocked(s, i) && (
                            <button type="button" className="remove" aria-label={`Remove seat ${i + 1}`} onClick={() => setSeats((rows) => rows.filter((r) => r.key !== s.key))}>
                              ×
                            </button>
                          )}
                        </span>
                      </SeatRow>
                    );
                  })}
                  </div>
                  {touched && emailBad && <ErrorText>One of the teammate emails doesn't look right.</ErrorText>}
                  <div className="hint">
                    {multi ? "Picking a plan above sets every seat. " : "Add a teammate to give them their own seat and credits. "}
                    {isLive ? "A seat with someone in it can change plan but not be removed." : "Teammates get their invite after payment."}
                  </div>
                </SeatsCard>
              )}

              {enterprise && (
                <EnterpriseStrip>
                  <div>
                    <b>{enterprise.name}</b> — custom credit volume, ATS or CRM integration and dedicated onboarding.
                  </div>
                  <LinkButton type="button" onClick={() => setDemoOpen(true)}>
                    Talk to us →
                  </LinkButton>
                </EnterpriseStrip>
              )}

              <div>
                <LinkButton type="button" aria-expanded={showCredits} onClick={() => setShowCredits((v) => !v)}>
                  {showCredits ? "Hide credit details" : "How do credits work?"}
                </LinkButton>
                {showCredits && (
                  <CreditsPanel>
                    <p>One balance for every tool. You're charged as each run goes, for what it actually uses. Balances top up on your renewal date; extra credits can be bought any time.</p>
                    <div className="grid">
                      {CREDIT_USES.map(([t, d]) => (
                        <div key={t}>
                          <b>{t}</b>
                          <span>{d}</span>
                        </div>
                      ))}
                    </div>
                  </CreditsPanel>
                )}
              </div>
              </Stack>
            </>
          )}

          {step === 2 && (
            <>
              <AddOn aria-labelledby="addon-credits">
                <div className="row">
                  <div className="copy">
                    <div className="eyebrow">One-off top-up</div>
                    <h2 id="addon-credits">Extra credits</h2>
                    <p className="desc">Handy for a busy first month. Spent the same way as your plan's credits.</p>
                  </div>
                  <div className="controls">
                    <Counter>
                      <button type="button" onClick={() => setBlocks((n) => Math.max(0, n - EXTRA_STEP))} disabled={blocks <= 0} aria-label="Fewer credits">
                        −
                      </button>
                      <output>{formatCredits(extraCredits)}</output>
                      <button type="button" onClick={() => setBlocks((n) => Math.min(MAX_BLOCKS, n + EXTRA_STEP))} disabled={blocks >= MAX_BLOCKS} aria-label="More credits">
                        +
                      </button>
                    </Counter>
                    <div className="cost">
                      <b>{usd(extraCost)}</b> · {usd(TOPUP_BLOCK_USD)} per {formatCredits(TOPUP_BLOCK_CREDITS)}
                    </div>
                  </div>
                </div>
                <Balance>
                  <div className="top">
                    <span>First-month balance</span>
                    <b>{formatCredits(planCredits + extraCredits)} credits</b>
                  </div>
                  <div className="bar" aria-hidden="true">
                    <i className="plan" style={{ width: `${(planCredits / (planCredits + extraCredits)) * 100}%` }} />
                    <i className="extra" style={{ width: `${(extraCredits / (planCredits + extraCredits)) * 100}%` }} />
                  </div>
                  <div className="sub">
                    {formatCredits(planCredits)} from your plan{extraCredits ? ` + ${formatCredits(extraCredits)} extra` : ""} · roughly {formatCredits(Math.floor((planCredits + extraCredits) / 500))}–
                    {formatCredits(Math.floor((planCredits + extraCredits) / 100))} searches
                  </div>
                </Balance>
              </AddOn>

              {!inhouse && (
                <AddOn aria-labelledby="addon-tracking">
                  <div className="row">
                    <div className="copy">
                      <div className="eyebrow">Monthly</div>
                      <h2 id="addon-tracking">Tracked companies</h2>
                      <p className="desc">Pick the firms you want job postings from. We'll confirm the list after payment, then new openings land in your Jobs tab daily.</p>
                      <div className="more">
                        <LinkButton type="button" onClick={() => setDemoOpen(true)}>
                          Prefer a walkthrough? Book a demo
                        </LinkButton>
                      </div>
                    </div>
                    <div className="controls">
                      <Counter>
                        <button type="button" onClick={() => setFirms((n) => Math.max(0, n - 1))} disabled={firms <= 0} aria-label="Fewer firms">
                          −
                        </button>
                        <output>{plural(firms, "firm")}</output>
                        <button type="button" onClick={() => setFirms((n) => Math.min(MAX_FIRMS, n + 1))} disabled={firms >= MAX_FIRMS} aria-label="More firms">
                          +
                        </button>
                      </Counter>
                      <div className="cost">
                        {usd(ADDON_MONTHLY_USD)} / firm / month
                        <br />+ {usd(ADDON_SETUP_USD)} setup per firm
                      </div>
                    </div>
                  </div>
                </AddOn>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <Covered aria-labelledby="covered-title">
                <div className="head">
                  <h2 id="covered-title">Who's covered</h2>
                  <LinkButton type="button" onClick={() => go(1)}>
                    {teamOn ? "Edit seats" : "Change plan"}
                  </LinkButton>
                </div>
                {seats.map((s, i) => {
                  const p = planOf(s.plan);
                  return (
                    <SeatRow key={s.key}>
                      <Avatar aria-hidden="true">{seatInitials(s, i)}</Avatar>
                      <div className="who">
                        <b>{seatTitle(s, i)}</b>
                        <small>{seatSub(s, i)}</small>
                      </div>
                      <span className="plan">{p.name}</span>
                      <span className="price">{usd(perSeatMonth(p, yearly))}/mo</span>
                    </SeatRow>
                  );
                })}
              </Covered>

              <Card>
                <Field>
                  <span className="label">Send invoices to</span>
                  <input
                    type="email"
                    value={invoice.email}
                    onChange={(e) => invoice.setEmail(e.target.value)}
                    disabled={!invoice.loaded}
                    autoComplete="email"
                    aria-invalid={touched && !invoice.ok ? "true" : undefined}
                  />
                  <small>Receipts and invoices go here. Your login email stays the same.</small>
                </Field>
              </Card>

              <Card>
                <Field as="div">
                  <label className="label" htmlFor="promo-code">
                    Promo code
                  </label>
                  {promo.applied ? (
                    <PromoTag>
                      <FiCheck aria-hidden="true" />
                      <b>{promo.applied.code}</b>
                      <span className="off">{`−${usd(promoOff)} today`}</span>
                      <LinkButton type="button" onClick={promo.clear}>
                        Remove
                      </LinkButton>
                    </PromoTag>
                  ) : (
                    <PromoRow>
                      <input
                        id="promo-code"
                        type="text"
                        value={promo.text}
                        onChange={(e) => promo.setText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            promo.apply();
                          }
                        }}
                        placeholder="Have a code?"
                        autoComplete="off"
                        autoCapitalize="characters"
                        spellCheck={false}
                        aria-invalid={promo.error ? "true" : undefined}
                        aria-describedby={promo.error ? "promo-error" : undefined}
                      />
                      <Button type="button" onClick={promo.apply} disabled={promo.busy || !promo.text.trim()}>
                        {promo.busy ? "Checking…" : "Apply"}
                      </Button>
                    </PromoRow>
                  )}
                  {promo.error ? (
                    <PromoError id="promo-error" role="alert">
                      {promo.error}
                    </PromoError>
                  ) : (
                    <small>{promo.applied ? "Taken off today's payment. Renewals are at the regular price." : "Applies to today's payment only."}</small>
                  )}
                </Field>
              </Card>

              <Note>
                Next you'll enter payment details on a secure Airwallex page. Cards, Apple Pay and Google Pay are accepted.
                {firms > 0 ? " We'll be in touch for your list of companies as soon as the plan is active." : ""}
              </Note>
            </>
          )}
        </Main>

        <Aside $fill>
          {isLive ? (
            <Order
              billingLabel={yearly ? "Yearly billing" : "Monthly billing"}
              lines={lines}
              dueLabel="New total"
              due={usd(recurring)}
              dueUnit={yearly ? "/yr" : "/mo"}
              then={{
                label: `Now · ${tierLabel(live.seatList || [])}, ${liveYearly ? "yearly" : "monthly"}`,
                amount: `${usd(liveAmount)}${liveYearly ? "/yr" : "/mo"}`,
                sub: "What you pay today is worked out on the next step.",
              }}
              credits={`${formatCredits(planCredits)}${multi ? ` · ${plural(seats.length, "seat")}` : ""}`}
              cta={{ label: ctaLabel, onClick: onNext, disabled: ctaDisabled, pending: false }}
              note={ctaNote}
            />
          ) : (
            <Order
              billingLabel={yearly ? "Yearly billing" : "Monthly billing"}
              lines={lines}
              due={usd(due)}
              then={{
                label: yearly ? `Renews ${nextRenewal(true)}` : `From ${nextRenewal(false)}`,
                amount: `${usd(recurring)}${yearly ? "/yr" : "/mo"}`,
                sub: yearly && firmsMonthly > 0 ? `Plus ${usd(firmsMonthly)}/month for tracked companies, charged monthly.` : null,
              }}
              credits={`${formatCredits(planCredits)}${multi ? ` · ${plural(seats.length, "seat")}` : ""}`}
              cta={{ label: ctaLabel, onClick: onNext, disabled: ctaDisabled, pending: hosted.pending }}
              note={ctaNote}
              error={hosted.error}
            >
              {step === 3 && <Terms checked={agreed} onChange={setAgreed} />}
            </Order>
          )}
        </Aside>
      </Layout>
    </FlowPage>
  );
}

/* One-off extra credits. Alone, one counter for the buyer's balance. With team seats, one
   counter per person who holds a seat: each share lands in that person's wallet, all on the
   buyer's card. `forUserId` preselects a colleague (the Team tab links that way). */
function TopupView({ startBlocks, forUserId, forName, teamOn }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { balance: walletBalance } = useCredits();
  const team = useTeam(teamOn);
  const hosted = useHostedCheckout();
  const [agreed, setAgreed] = React.useState(false);
  // Blocks per person, keyed by userId ("" is the buyer). Starts with the buyer's (or the preselected colleague's) share.
  const [shares, setShares] = React.useState(() => ({ [forUserId || ""]: Math.max(1, startBlocks || 1) }));
  // The people credits can go to: everyone with a seat, the buyer first.
  const people = React.useMemo(() => {
    const list = (team?.members || []).filter((m) => m.role === "owner" || m.seatId);
    return [...list.filter((m) => m.role === "owner"), ...list.filter((m) => m.role !== "owner")];
  }, [team]);
  const isMe = (id) => !id || id === user?.sub || people.find((m) => m.userId === id)?.role === "owner";
  const keyOf = (m) => (m.role === "owner" ? "" : m.userId);
  const blocksOf = (key) => shares[key] || 0;
  const setBlocksOf = (key, n) => setShares((cur) => ({ ...cur, [key]: Math.max(0, Math.min(MAX_BLOCKS, n)) }));
  const multi = people.length > 1;
  // What goes where, in the order people are listed; a solo account is just the buyer's row.
  const lines = multi
    ? people.filter((m) => blocksOf(keyOf(m)) > 0).map((m) => ({ key: keyOf(m) || "me", userId: keyOf(m), name: isMe(m.userId) ? "You" : m.name || m.email, blocks: blocksOf(keyOf(m)) }))
    : [{ key: "me", userId: forUserId || "", name: forUserId && !isMe(forUserId) ? forName || "your colleague" : "You", blocks: blocksOf(forUserId || "") }];
  const totalBlocks = lines.reduce((n, l) => n + l.blocks, 0);
  const total = totalBlocks * TOPUP_BLOCK_USD;
  const walletNow = Math.max(0, typeof walletBalance === "number" ? walletBalance : 0);
  const others = lines.filter((l) => !isMe(l.userId));
  const backTo = forUserId ? "/settings?tab=team" : "/settings?tab=credits";

  const pay = () => {
    if (!agreed) return hosted.setError("Please agree to the terms and privacy policy to continue.");
    if (totalBlocks < 1) return hosted.setError("Choose how many credits to buy.");
    const allocations = lines.map((l) => ({ userId: isMe(l.userId) ? null : l.userId, blocks: l.blocks }));
    return hosted.start("/api/billing/topup", { blocks: totalBlocks, agreed, allocations });
  };

  const counter = (key, min) => (
    <Counter>
      <button type="button" onClick={() => setBlocksOf(key, blocksOf(key) - 1)} disabled={blocksOf(key) <= min} aria-label="Fewer credits">
        −
      </button>
      <output>{formatCredits(blocksOf(key) * TOPUP_BLOCK_CREDITS)}</output>
      <button type="button" onClick={() => setBlocksOf(key, blocksOf(key) + 1)} disabled={blocksOf(key) >= MAX_BLOCKS} aria-label="More credits">
        +
      </button>
    </Counter>
  );
  // Rough reach of the credits being bought: searches cost roughly 100–500 each.
  const reach = (credits) => `${formatCredits(Math.floor(credits / 500))}–${formatCredits(Math.floor(credits / 100))}`;
  const soloKey = forUserId || "";
  const soloBlocks = blocksOf(soloKey);

  return (
    <FlowPage>
      <style>{`@keyframes ck-spin{to{transform:rotate(360deg)}}`}</style>
      <Head>
        <BackLink type="button" onClick={() => navigate(backTo)}>
          ← {forUserId ? "Back to Team" : "Back to Credits & Usage"}
        </BackLink>
        <h1>
          Extra <em>credits.</em>
        </h1>
        <p>
          {multi
            ? "A one-off top-up. Choose how many credits each person gets; every share lands in their own balance as soon as it clears, all charged to you."
            : others.length
              ? `A one-off top-up for ${others[0].name}, added to their balance as soon as it clears. Charged to you.`
              : "A one-off top-up, added to your balance as soon as it clears."}
        </p>
      </Head>
      <Layout $wide $fill>
        <Main>
          {multi ? (
            <Covered aria-labelledby="topup-who">
              <div className="head">
                <h2 id="topup-who">Who gets the credits</h2>
                <span style={{ fontSize: 13, opacity: 0.7 }}>
                  {usd(TOPUP_BLOCK_USD)} per {formatCredits(TOPUP_BLOCK_CREDITS)}
                </span>
              </div>
              {people.map((m) => {
                const key = keyOf(m);
                const me = key === "";
                return (
                  <SeatRow key={m.userId}>
                    <Avatar aria-hidden="true">{initialsOf(m.name, m.email)}</Avatar>
                    <div className="who">
                      <b>{me ? "You" : m.name || m.email}</b>
                      <small>{me ? user?.email : m.name ? m.email : m.planName || ""}</small>
                    </div>
                    {counter(key, 0)}
                    <span className="price">{blocksOf(key) ? usd(blocksOf(key) * TOPUP_BLOCK_USD) : "—"}</span>
                  </SeatRow>
                );
              })}
            </Covered>
          ) : (
            <Picker aria-labelledby="topup-title">
              <div className="head">
                <h2 id="topup-title">How many credits?</h2>
                <span>
                  {usd(TOPUP_BLOCK_USD)} per {formatCredits(TOPUP_BLOCK_CREDITS)} · up to {formatCredits(MAX_BLOCKS * TOPUP_BLOCK_CREDITS)} at a time
                </span>
              </div>
              <div className="tiles" role="radiogroup" aria-label="Amount">
                {TOPUP_PRESETS.map((b) => {
                  const on = soloBlocks === b;
                  return (
                    <AmountTile key={b} type="button" role="radio" aria-checked={on} $on={on} onClick={() => setBlocksOf(soloKey, b)}>
                      <span className="n">
                        {formatCredits(b * TOPUP_BLOCK_CREDITS)}
                        <small>credits</small>
                      </span>
                      <span className="usd">{usd(b * TOPUP_BLOCK_USD)}</span>
                      {b === 10 && <span className="tag">Most common</span>}
                    </AmountTile>
                  );
                })}
              </div>
              <div className="custom">
                <div className="label">
                  Or choose exactly
                  <small>In steps of {formatCredits(TOPUP_BLOCK_CREDITS)}.</small>
                </div>
                <div className="right">
                  {counter(soloKey, 1)}
                  <span className="cost">{usd(total)}</span>
                </div>
              </div>
            </Picker>
          )}
          {totalBlocks > 0 && (
            <Landing aria-labelledby="landing-title">
              <div className="head">
                <h2 id="landing-title">{lines.length > 1 ? "Where the credits land" : "Balance after this top-up"}</h2>
                <span>Added the moment the payment clears · never expire</span>
              </div>
              {lines.map((l) => {
                const me = isMe(l.userId);
                const person = me ? null : people.find((m) => m.userId === l.userId);
                const now = me ? walletNow : typeof person?.balance === "number" ? Math.max(0, person.balance) : null;
                const add = l.blocks * TOPUP_BLOCK_CREDITS;
                const email = me ? user?.email : person?.email || "";
                return (
                  <div className="row" key={l.key}>
                    <div className="who">
                      <b>{l.name}</b>
                      <small>{email || (me ? "" : "Teammate")}</small>
                    </div>
                    <div className="add">
                      <span className="chip">+{formatCredits(add)}</span>
                    </div>
                    <div className="move">
                      <span className="after">
                        {now != null ? formatCredits(Math.round(now + add)) : formatCredits(add)}
                        <small>credits</small>
                      </span>
                    </div>
                  </div>
                );
              })}
              <div className="foot">Roughly {reach(totalBlocks * TOPUP_BLOCK_CREDITS)} more candidate searches in all, depending on how wide each one runs.</div>
            </Landing>
          )}
        </Main>
        <Aside $fill>
          <Order
            lines={lines.map((l) => ({ key: l.key, label: l.name === "You" ? "Extra credits" : `Extra credits for ${l.name}`, sub: `${formatCredits(l.blocks * TOPUP_BLOCK_CREDITS)} credits, one-off`, amount: usd(l.blocks * TOPUP_BLOCK_USD) }))}
            due={usd(total)}
            cta={{ label: hosted.pending ? "Opening secure checkout…" : `Pay ${usd(total)}`, onClick: pay, disabled: hosted.pending || !agreed || totalBlocks < 1, pending: hosted.pending }}
            note="Secured by Airwallex"
            error={hosted.error}
          >
            <Terms checked={agreed} onChange={setAgreed} />
          </Order>
        </Aside>
      </Layout>
    </FlowPage>
  );
}

// Back from Airwallex: poll until the payment has been applied.
function DoneView({ topup, forWho = "" }) {
  const navigate = useNavigate();
  const { checkSession } = useAuth();
  const { balance } = useCredits();
  const [state, setState] = React.useState("waiting"); // waiting | ready | slow
  const [status, setStatus] = React.useState(null);
  const tries = React.useRef(0);

  React.useEffect(() => {
    let alive = true;
    let timer;
    const poll = async () => {
      tries.current += 1;
      try {
        const res = await fetchWithAuth(`/api/billing/status?sync=${tries.current <= 3 || tries.current % 5 === 0 ? 1 : 0}`);
        const body = await res.json().catch(() => ({}));
        if (!alive) return;
        if (res.ok) {
          setStatus(body);
          if (topup || body.status === "ACTIVE") {
            setState("ready");
            checkSession?.();
            creditsChanged(); // the purchase just landed on the wallet
            try {
              sessionStorage.removeItem(EMAILS_KEY);
            } catch {
              // nothing to clear
            }
            return;
          }
        }
      } catch {
        if (!alive) return;
      }
      if (tries.current >= DONE_POLL_LIMIT) return setState("slow");
      timer = setTimeout(poll, DONE_POLL_MS);
    };
    poll();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [topup, checkSession]);

  const ready = state === "ready";
  const planName = status?.planName;
  const renews = longDate(status?.renewsAt);
  const seats = status?.seats;

  const headline = () => {
    if (state === "waiting") return <>Confirming your <em>payment</em>.</>;
    if (state === "slow") return <>Still confirming your <em>payment</em>.</>;
    if (topup) return <>Credits <em>added</em>.</>;
    if (planName) return <>You're on <em>{planName}</em>.</>;
    return <>You're all <em>set</em>.</>;
  };
  const lede = () => {
    if (state === "waiting") return "This usually takes a few seconds. Keep this page open and your plan will appear here the moment it lands.";
    if (state === "slow") return "The payment is taking longer than usual to reach us. Your account will update on its own, and nothing else is needed from you.";
    if (topup) {
      return forWho
        ? `They're on ${forWho}'s balance now, ready to spend on searches, lookups and drafts. Your plan and renewal date are unchanged.`
        : "They're in the balances you chose, ready to spend on searches, lookups and drafts. Your plan and renewal date are unchanged.";
    }
    return `Your plan is live. Credits refresh on your renewal date each month, and anything extra you added today is already in your balance.${seats > 1 ? " Teammates you named have their invite on the way." : ""}`;
  };

  const stat = (label, value, hint) => (
    <Stat>
      <div className="label">{label}</div>
      {value == null ? <div className="value pending" /> : <div className="value">{value}</div>}
      {hint && <div className="hint">{hint}</div>}
    </Stat>
  );

  return (
    <Page style={{ maxWidth: "none" }}>
      <style>{`@keyframes ck-spin{to{transform:rotate(360deg)}}@keyframes ck-pulse{0%,100%{opacity:.55}50%{opacity:1}}`}</style>
      <DonePage>
        <DoneHero $tone={ready ? "ok" : "wait"}>
          <span className="eyebrow">
            {ready ? (
              <>
                <i>
                  <FiCheck size={12} strokeWidth={3} />
                </i>
                Payment confirmed
              </>
            ) : (
              <>
                <span style={{ ...spin, marginRight: 0 }} />
                {state === "slow" ? "Taking longer than usual" : "Talking to your bank"}
              </>
            )}
          </span>
          <h1>{headline()}</h1>
          <p className="lede">{lede()}</p>

          <Stats>
            {stat("Credits/month", ready && status?.creditsPerMonth ? formatCredits(status.creditsPerMonth) : ready ? "—" : null, seats > 1 ? `Across ${seats} seats` : "Per seat, every month")}
            {stat("In your balance now", typeof balance === "number" && ready ? formatCredits(balance) : null, "Ready to spend today")}
            {stat("Plan", ready ? planName || "—" : null, ready && status?.cycle ? (status.cycle === "yearly" ? "Billed yearly" : "Billed monthly") : "Change it any time in Settings")}
            {stat("Renews on", ready ? renews || "—" : null, "Credits top back up on this date")}
          </Stats>

          <DoneActions>
            <Button type="button" $primary disabled={!ready} onClick={() => (window.location.href = "/")}>
              Open scoutm8
              <FiArrowRight size={16} style={{ marginLeft: 8, verticalAlign: -3 }} />
            </Button>
            <Button type="button" disabled={!ready} onClick={() => navigate("/settings?tab=billing")}>
              View invoice
            </Button>
          </DoneActions>
        </DoneHero>

        <NextSteps>
          <div className="head">
            <h2>
              Make the first one <em>count</em>.
            </h2>
            <p className="sub">Four things most recruiters do in their first hour.</p>
          </div>
          <StepGrid>
            {FIRST_STEPS.map(({ icon: Icon, title, text, cta, to }, i) => (
              <Step key={title} type="button" onClick={() => navigate(to)}>
                <span className="icon">
                  <Icon size={18} />
                </span>
                <span className="n">Step {i + 1}</span>
                <h3>{title}</h3>
                <p>{text}</p>
                <span className="go">
                  {cta}
                  <FiArrowRight size={14} />
                </span>
              </Step>
            ))}
          </StepGrid>
        </NextSteps>
      </DonePage>
    </Page>
  );
}

/* Reviewing a change to a live plan: no payment page. Upgrades charge the prorated difference
   to the saved card now; downgrades and cycle switches wait for the renewal date. */
function ChangeView({ seatSpec, cycle, onBack, top = null }) {
  const navigate = useNavigate();
  const [preview, setPreview] = React.useState(null);
  const [error, setError] = React.useState("");
  const [pending, setPending] = React.useState(false);
  const [result, setResult] = React.useState(null);
  // Existing seats go by id (their people stay in them); new ones carry just a plan.
  const seatsBody = React.useMemo(() => seatSpec.map((s) => (s.id ? { id: s.id, plan: s.plan } : { plan: s.plan })), [seatSpec]);
  const bodyKey = JSON.stringify({ cycle, seats: seatsBody });

  React.useEffect(() => {
    let alive = true;
    setPreview(null);
    setError("");
    fetchWithAuth("/api/billing/change/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: bodyKey,
    })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.error || "We couldn't work out that change just now.");
        if (alive) setPreview(body);
      })
      .catch((err) => alive && setError(err.message));
    return () => {
      alive = false;
    };
  }, [bodyKey]);

  const confirm = async () => {
    if (pending) return;
    setPending(true);
    setError("");
    try {
      const res = await fetchWithAuth("/api/billing/change", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: bodyKey,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "We couldn't change the plan just now.");
      setResult(body);
      creditsChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setPending(false);
    }
  };

  const immediate = preview?.kind === "immediate" || preview?.kind === "mixed";
  const mixed = preview?.kind === "mixed";
  const yearly = cycle === "yearly";
  const per = yearly ? "/yr" : "/mo";
  const targetLabel = preview?.target?.label || tierLabel(seatSpec);
  const tiers = tiersOf(seatSpec);
  const planCredits = seatSpec.reduce((n, s) => n + planOf(s.plan).credits, 0);
  const fallbackNext = tiers.reduce((n, [p, count]) => n + perSeatCharge(p, yearly) * count, 0);

  if (result) {
    const scheduled = result.kind === "scheduled";
    const mixedDone = result.kind === "mixed";
    const when = longDate(result.status?.pendingChange?.effectiveAt || result.preview?.effectiveAt);
    const interimLabel = result.preview?.interim?.label || targetLabel;
    return (
      <Page style={{ maxWidth: "none" }}>
        <DonePage>
          <DoneHero $tone="ok">
            <span className="eyebrow">
              <i>
                <FiCheck size={12} strokeWidth={3} />
              </i>
              {scheduled ? "Change scheduled" : mixedDone ? "Plan updated, rest scheduled" : result.kind === "kept" ? "Plan kept" : "Plan updated"}
            </span>
            <h1>
              {scheduled ? (
                <>
                  <em>{targetLabel}</em> from {when}.
                </>
              ) : mixedDone ? (
                <>
                  <em>{interimLabel}</em> now, <em>{targetLabel}</em> from {when}.
                </>
              ) : result.kind === "kept" ? (
                <>
                  Your plan <em>carries on</em>.
                </>
              ) : (
                <>
                  You're on <em>{targetLabel}</em>.
                </>
              )}
            </h1>
            <p className="lede">
              {scheduled
                ? `You won't be charged until ${when || "then"}; your current seats run until that date. You can undo this from Settings any time before it.`
                : mixedDone
                  ? `Today's part is done: the difference for the rest of this period is on your saved card and the credits for those days are in the right balances. The rest takes effect on ${when || "your renewal date"}; you can undo that part from Settings before then.`
                  : result.kind === "kept"
                  ? "The scheduled change was dropped and your current plan continues as before."
                  : "The difference for the rest of this period has been charged to your saved card, and the extra credits for those days are already in the right balances."}
            </p>
            <DoneActions>
              <Button type="button" $primary onClick={() => (window.location.href = "/")}>
                Open scoutm8
                <FiArrowRight size={16} style={{ marginLeft: 8, verticalAlign: -3 }} />
              </Button>
              <Button type="button" onClick={() => navigate("/settings?tab=team")}>
                Manage seats in Settings
              </Button>
            </DoneActions>
          </DoneHero>
        </DonePage>
      </Page>
    );
  }

  const pct = preview?.fraction != null ? Math.round(preview.fraction * 100) : null;
  const creditPct = preview?.creditFraction != null ? Math.round(preview.creditFraction * 100) : pct;
  const daysText = preview?.daysLeft != null ? `${preview.daysLeft} of ${preview.periodDays ?? "…"} days` : "the days";
  const lines = [
    {
      key: "today",
      label: immediate ? "Upgrade difference" : "No charge today",
      sub: immediate ? (pct != null ? `${daysText} left · ${pct}% of the difference` : "Rest of this period") : preview ? `Starts ${longDate(preview.effectiveAt) || "on your renewal date"}` : "Working out the cost…",
      amount: usd(preview?.today ?? 0),
    },
  ];
  const thenDate = longDate(preview?.kind === "scheduled" ? preview?.effectiveAt : preview?.renewsAt) || "renewal";
  const renewDate = longDate(preview?.renewsAt) || "your renewal date";
  const perMonth = (p) => (p.cycle === "yearly" ? Math.round(p.amount / 12) : p.amount);
  const monthlyCredits = (p) => p.seatList.reduce((n, s) => n + (planOf(s.plan)?.credits || 0), 0);
  const seatDelta = preview ? preview.target.seats - preview.current.seats : 0;
  const upgradedSeats = preview?.changed?.length || 0;
  const downSeats = preview?.downgraded?.length || 0;
  // What waits for the renewal date in a mixed change, in words.
  const laterBits = preview
    ? [
        downSeats > 0 ? `${plural(downSeats, "seat")} ${downSeats === 1 ? "moves" : "move"} down to ${[...new Set(preview.downgraded.map((d) => planOf(d.to).name))].join(" and ")}` : "",
        preview.removed > 0 ? `${plural(preview.removed, "seat")} dropped` : "",
        preview.cycleChange ? `billing switches to ${preview.target.cycle}` : "",
      ].filter(Boolean)
    : [];

  return (
    <FlowPage>
      <style>{`@keyframes ck-spin{to{transform:rotate(360deg)}}`}</style>
      {top}
      <Head>
        <BackLink type="button" onClick={onBack}>
          ← Back
        </BackLink>
        <h1>
          Review your <em>change.</em>
        </h1>
        <p>
          {mixed
            ? "Added seats and upgrades start today on your saved card; moving a seat down, dropping a seat or switching how you're billed waits for your renewal date."
            : immediate
              ? "Upgrades and added seats start right away on your saved card."
              : "Moving a seat down, dropping a seat or switching how you're billed starts on your renewal date."}
        </p>
      </Head>
      <Layout $wide $fill>
        <Main $fill>
          <Card>
            <CardTitle>Your plan</CardTitle>
            {!preview && !error && <p style={{ margin: "8px 0 0", fontSize: 13, opacity: 0.7 }}>Working out the cost…</p>}
            {preview && (
              <Compare $three={mixed}>
                <div className="col now">
                  <span className="eyebrow">Now</span>
                  <span className="tier">{preview.current.label}</span>
                  <span className="meta">
                    {plural(preview.current.seats, "seat")} · billed {preview.current.cycle}
                  </span>
                  <span className="price">
                    {usd(preview.current.amount)}
                    <small>{preview.current.cycle === "yearly" ? "/yr" : "/mo"}</small>
                  </span>
                  <span className="credits">{formatCredits(monthlyCredits(preview.current))} credits a month</span>
                </div>
                <span className="arrow" aria-hidden="true">
                  <FiArrowRight size={20} />
                </span>
                {mixed && (
                  <>
                    <div className="col next">
                      <span className="eyebrow">From today</span>
                      <span className="tier">{preview.interim.label}</span>
                      <span className="meta">
                        {plural(preview.interim.seats, "seat")} · billed {preview.interim.cycle}
                      </span>
                      <span className="delta">
                        {[seatDelta > 0 ? `+${plural(seatDelta, "seat")}` : "", upgradedSeats > 0 ? `${plural(upgradedSeats, "seat")} moving up` : ""].filter(Boolean).join(" · ")}
                      </span>
                      <span className="price">
                        {usd(preview.interim.amount)}
                        <small>{preview.interim.cycle === "yearly" ? "/yr" : "/mo"}</small>
                      </span>
                      <span className="credits">{formatCredits(monthlyCredits(preview.interim))} credits a month</span>
                    </div>
                    <span className="arrow" aria-hidden="true">
                      <FiArrowRight size={20} />
                    </span>
                  </>
                )}
                <div className="col next">
                  <span className="eyebrow">{immediate && !mixed ? "From today" : `From ${longDate(preview.effectiveAt) || "renewal"}`}</span>
                  <span className="tier">{preview.target.label}</span>
                  <span className="meta">
                    {plural(preview.target.seats, "seat")} · billed {preview.target.cycle}
                  </span>
                  {(seatDelta !== 0 || upgradedSeats > 0 || downSeats > 0 || preview.cycleChange) && (
                    <span className="delta">
                      {[
                        !mixed && seatDelta > 0 ? `+${plural(seatDelta, "seat")}` : "",
                        preview.removed > 0 ? `−${plural(preview.removed, "seat")}` : "",
                        !mixed && upgradedSeats > 0 ? `${plural(upgradedSeats, "seat")} moving up` : "",
                        downSeats > 0 ? `${plural(downSeats, "seat")} moving down` : "",
                        preview.cycleChange ? `switching to ${preview.target.cycle}` : "",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  )}
                  <span className="price">
                    {usd(preview.target.amount)}
                    <small>{preview.target.cycle === "yearly" ? "/yr" : "/mo"}</small>
                    {preview.target.cycle === "yearly" && <small>{` · ${usd(perMonth(preview.target))}/mo`}</small>}
                  </span>
                  <span className="credits">{formatCredits(monthlyCredits(preview.target))} credits a month</span>
                </div>
              </Compare>
            )}
          </Card>

          {preview && (
            <Card>
              <CardTitle>What happens</CardTitle>
              <Timeline>
                {immediate ? (
                  <>
                    <li className="now">
                      <span className="dot" aria-hidden="true" />
                      <span className="when">Today</span>
                      <div className="what">
                        <b>
                          {preview.today > 0 ? `${usd(preview.today)} charged to your saved card` : "Nothing to pay today"}
                          {mixed ? ` for ${[seatDelta > 0 ? plural(seatDelta, "new seat") : "", upgradedSeats > 0 ? plural(upgradedSeats, "upgrade") : ""].filter(Boolean).join(" and ")}` : ""}
                        </b>
                        <p>
                          {pct != null
                            ? `You pay ${pct}% of the price difference: ${daysText} are left in this period. ${mixed ? `Until ${renewDate} your plan is ${preview.interim.label} at ${usd(preview.interim.amount)}${preview.interim.cycle === "yearly" ? "/yr" : "/mo"}.` : `The new price starts on ${renewDate} and is never charged twice.`}`
                            : "The price difference for the rest of this period. The new price starts on your renewal date."}
                        </p>
                        {pct != null && (
                          <span className="bar" aria-hidden="true">
                            <i style={{ width: `${pct}%` }} />
                          </span>
                        )}
                      </div>
                    </li>
                    {preview.creditsNow > 0 && (
                      <li className="ok">
                        <span className="dot" aria-hidden="true" />
                        <span className="when">Today</span>
                        <div className="what">
                          <b>+{formatCredits(preview.creditsNow)} credits for the upgraded {plural(upgradedSeats, "seat")}</b>
                          <p>
                            {creditPct}% of the {formatCredits(preview.creditsFull)} extra credits a tier up brings, matching the days left. They land in those balances as soon as you confirm; the full
                            allowance comes with every renewal.
                          </p>
                        </div>
                      </li>
                    )}
                    {preview.added > 0 && (
                      <li className="ok">
                        <span className="dot" aria-hidden="true" />
                        <span className="when">When a seat is filled</span>
                        <div className="what">
                          <b>
                            {plural(preview.added, "new seat")} · {formatCredits(preview.addedCreditsNow)} credits paid for today
                          </b>
                          <p>
                            {creditPct}% of the allowance, matching the days you are paying for. Invite someone from Settings → Team: whoever takes the seat before {renewDate} gets those
                            credits the moment they join, then the full allowance every renewal.
                          </p>
                        </div>
                      </li>
                    )}
                    <li>
                      <span className="dot" aria-hidden="true" />
                      <span className="when">{renewDate}</span>
                      <div className="what">
                        <b>
                          {mixed ? `${preview.target.label} · ` : ""}
                          {usd(preview.nextCharge?.amount ?? preview.target.amount)}
                          {per} from your renewal
                        </b>
                        <p>
                          {mixed ? `${laterBits.join(", ")} on this date, nothing refunded or charged for it. ` : ""}
                          The full price for {preview.target.label}, and {formatCredits(monthlyCredits(preview.target))} credits refresh every month
                          {preview.nextCharge?.maintenanceMonthly > 0 ? `, plus ${usd(preview.nextCharge.maintenanceMonthly)}/month for tracked companies` : ""}.
                          {mixed ? " You can undo this part from Settings before then." : ""}
                        </p>
                      </div>
                    </li>
                  </>
                ) : (
                  <>
                    <li className="now">
                      <span className="dot" aria-hidden="true" />
                      <span className="when">Today</span>
                      <div className="what">
                        <b>Nothing changes yet</b>
                        <p>
                          Your current {plural(preview.current.seats, "seat")} and credits run until {longDate(preview.effectiveAt) || "your renewal date"}. Nothing is charged today, and you can
                          undo this from Settings any time before then.
                        </p>
                      </div>
                    </li>
                    <li className="ok">
                      <span className="dot" aria-hidden="true" />
                      <span className="when">{longDate(preview.effectiveAt) || "Renewal"}</span>
                      <div className="what">
                        <b>
                          {preview.target.label} starts · {usd(preview.nextCharge?.amount ?? preview.target.amount)}
                          {per}
                        </b>
                        <p>
                          Charged to your saved card on that date, with {formatCredits(monthlyCredits(preview.target))} credits a month from then on
                          {preview.removed > 0 ? `. ${plural(preview.removed, "seat")} dropped` : ""}
                          {preview.nextCharge?.maintenanceMonthly > 0 ? `, plus ${usd(preview.nextCharge.maintenanceMonthly)}/month for tracked companies` : ""}.
                        </p>
                      </div>
                    </li>
                  </>
                )}
              </Timeline>
            </Card>
          )}
        </Main>
        <Aside $fill>
          <Order
            billingLabel={`${yearly ? "Yearly" : "Monthly"} billing · ${plural(seatSpec.length, "seat")}`}
            lines={lines}
            due={usd(preview?.today ?? 0)}
            then={{
              label: `From ${thenDate}`,
              amount: `${usd(preview?.nextCharge?.amount ?? fallbackNext)}${per}`,
              sub: yearly && preview?.nextCharge?.maintenance > 0 ? `Plus ${usd(preview.nextCharge.maintenance)}/month for tracked companies, charged monthly.` : null,
            }}
            credits={`${formatCredits(planCredits)}${seatSpec.length > 1 ? ` · ${plural(seatSpec.length, "seat")}` : ""}`}
            cta={{ label: pending ? "Applying…" : immediate ? "Confirm and pay" : "Confirm change", onClick: confirm, disabled: pending || !preview, pending }}
            note={immediate ? "Charged to your saved card" : "Nothing is charged today"}
            error={error}
          />
        </Aside>
      </Layout>
    </FlowPage>
  );
}

export default function CheckoutPage() {
  const q = useQuery();
  const { features: appFeatures } = useAuth();
  const { status, loading } = usePlan();
  const teamOn = Boolean(appFeatures?.teamSeats); // colleague and seat links only mean something with team seats on
  const topupOnly = q.get("topup") === "1";
  // The admin buying credits for a colleague's seat: their wallet, not the payer's.
  const forUserId = (teamOn && q.get("for")) || "";
  const forName = (teamOn && q.get("forName")) || "";
  const yearly = q.get("cycle") === "yearly";
  const seatSpec = React.useMemo(() => parseSeats(q.get("seats"), q.get("plan")), [q]);

  if (q.get("done") === "1") return <DoneView topup={topupOnly} forWho={forUserId ? forName || "your colleague" : ""} />;
  if (topupOnly) return <TopupView startBlocks={Number(q.get("blocks")) || 1} forUserId={forUserId} forName={forName} teamOn={teamOn} />;
  // The plan decides which flow this is, so wait for it rather than start one and swap.
  if (loading) return <Page />;
  const live = status?.plan && ["ACTIVE", "UNPAID"].includes(status.status) ? status : null;
  return (
    <PlanFlow
      key={live ? "live" : "new"}
      seatSpec={seatSpec}
      startYearly={yearly}
      startBlocks={Math.max(0, Math.min(MAX_BLOCKS, Number(q.get("blocks")) || 0))}
      startFirms={Math.max(0, Math.min(MAX_FIRMS, Number(q.get("firms")) || 0))}
      teamOn={teamOn}
      live={live}
    />
  );
}
